import { makeObservable, observable } from 'mobx'

import { LocationType } from '~/client/graph'
import MapViewLocationIcon from '~/client/src/shared/enums/SitemapAttributeIcon'
import IHierarchyParent from '~/client/src/shared/types/IHierarchyParent'
import ILocationAttributeDto, {
  IWorkflowRestrictions,
} from '~/client/src/shared/types/ILocationAttributeDto'
import IMsDateInterval from '~/client/src/shared/types/IMsDateInterval'
import { DEFAULT_SITEMAP_ITEM_COLOR } from '~/client/src/shared/utils/SitemapItemsColors'

import {
  areObjectArraysEqual,
  copyObject,
  copyObjectArray,
} from '../../utils/util'
import LocationBase from './LocationBase'

// TODO: Consider simplification of inheritance logic and using of generics.
// Also consider renaming LocationAttributeBase into something related with deliveries
// since properties "maxBookingDuration" and "maxOverlappingDeliveries" refer to Delivery.

export default abstract class LocationAttributeBase<
  T extends LocationAttributeBase<T> = any,
>
  extends LocationBase<T>
  implements ILocationAttributeDto
{
  public static factory<U>(
    model: new (...args: any) => U,
    dto: ILocationAttributeDto,
    defaultIcon: MapViewLocationIcon,
  ): U {
    const instance = new model(
      dto.id,
      dto.name,
      dto.color || DEFAULT_SITEMAP_ITEM_COLOR,
      dto.iconName || defaultIcon,
      dto.projectId,
      dto.code,
      Object.values(dto.operatingIntervals || {}),
      dto.parent,
      Object.values(dto.permittedCompanies || {}),
      dto.isDeleted,
      Object.values(dto.assignedSitemaps || {}),
      Object.values(dto.assignedGlobes || {}),
      dto.maxBookingDuration,
      dto.maxOverlappingDeliveries,
      dto.workflowRestrictions,
    )
    // showInMap lives outside the positional constructor; overlay it when the DTO
    // provides it, otherwise keep the field's default (true).
    if (typeof dto.showInMap === 'boolean') {
      ;(instance as unknown as LocationBase).showInMap = dto.showInMap
    }
    if (typeof dto.isBookable === 'boolean') {
      ;(instance as unknown as LocationBase).isBookable = dto.isBookable
    }
    // templateId and data likewise, exactly as LocationBase.factory does. Omitting them here
    // was not harmless: the model kept data = null, so getDto()'s dataWithKind re-synthesised
    // `{ type: <kind> }` as the WHOLE data object, and the save handler preserves the stored
    // payload only when data arrives null - so renaming a zone erased every custom property
    // on it. The two factories differ only in their positional arguments; anything outside
    // those has to be overlaid in both.
    ;(instance as unknown as LocationBase).templateId = dto.templateId ?? null
    ;(instance as unknown as LocationBase).data = dto.data ?? null
    ;(instance as unknown as LocationBase).stxResourceId =
      dto.stxResourceId || null
    return instance
  }

  @observable public maxBookingDuration: number = 0
  @observable public maxOverlappingDeliveries: number = 0
  @observable public workflowRestrictions: IWorkflowRestrictions = null
  public abstract type: LocationType
  protected abstract model: new (...args: any) => T

  public constructor(
    id: string,
    name: string,
    color: string,
    iconName: MapViewLocationIcon,
    projectId: string,
    code?: string,
    operatingIntervals: IMsDateInterval[] = [],
    parent?: IHierarchyParent,
    permittedCompanies: string[] = [],
    isDeleted: boolean = false,
    assignedSitemaps: string[] = [],
    assignedGlobes: string[] = [],
    maxBookingDuration: number = 0,
    maxOverlappingDeliveries: number = 0,
    workflowRestrictions: IWorkflowRestrictions = null,
  ) {
    super(
      id,
      name,
      color,
      iconName,
      projectId,
      code,
      operatingIntervals,
      parent,
      permittedCompanies,
      isDeleted,
      assignedSitemaps,
      assignedGlobes,
    )
    this.maxBookingDuration = maxBookingDuration
    this.maxOverlappingDeliveries = maxOverlappingDeliveries
    this.workflowRestrictions = workflowRestrictions
    makeObservable(this)
  }

  public isEqual(dto: T) {
    return (
      super.isEqual(dto) &&
      this.maxBookingDuration === dto.maxBookingDuration &&
      this.maxOverlappingDeliveries === dto.maxOverlappingDeliveries &&
      areObjectArraysEqual(
        this.workflowRestrictions?.maxBookingDurations || [],
        dto.workflowRestrictions?.maxBookingDurations || [],
      ) &&
      areObjectArraysEqual(
        this.workflowRestrictions?.maxOverlappingBookings || [],
        dto.workflowRestrictions?.maxOverlappingBookings || [],
      )
    )
  }

  public copy(): T {
    const item = new this.model(
      this.id,
      this.name,
      this.color,
      this.iconName,
      this.projectId,
      this.code,
      copyObjectArray(this.operatingIntervals),
      this.parent && copyObject(this.parent),
      this.permittedCompanies,
      this.isDeleted,
      this.assignedSitemaps,
      this.assignedGlobes,
      this.maxBookingDuration,
      this.maxOverlappingDeliveries,
      {
        maxBookingDurations:
          this.workflowRestrictions?.maxBookingDurations?.slice() || [],
        maxOverlappingBookings:
          this.workflowRestrictions?.maxOverlappingBookings?.slice() || [],
      },
    )
    // Not a constructor param — carry it over so copies (used by map renderers)
    // keep the hidden state.
    ;(item as unknown as LocationBase).showInMap = this.showInMap
    ;(item as unknown as LocationBase).isBookable = this.isBookable
    // Not constructor params — carry them over, the same as showInMap. A field
    // added outside the positional constructor has to be handled in BOTH factory()
    // and copy(); missing copy() is invisible until something copies before saving,
    // and the map save does exactly that (selectedMapViewItem.copy()), which silently
    // dropped the kind and re-derived the root from the legacy enum.
    ;(item as unknown as LocationBase).templateId = this.templateId ?? null
    ;(item as unknown as LocationBase).data = this.data
      ? { ...this.data }
      : null
    ;(item as unknown as LocationBase).stxResourceId = this.stxResourceId
    return item
  }

  public getDto(): ILocationAttributeDto {
    return Object.assign(super.getDto(), {
      maxBookingDuration: this.maxBookingDuration,
      maxOverlappingDeliveries: this.maxOverlappingDeliveries,
    })
  }
}
